import { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ScissorsIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import type { NIBValidationResult } from '@/types';

interface ValidationResultsProps {
  validationResult: NIBValidationResult | null;
  onRecordClick?: (recordId: string) => void;
}

export function ValidationResults({
  validationResult,
  onRecordClick,
}: ValidationResultsProps) {
  const [showErrors, setShowErrors] = useState(true);
  const [showWarnings, setShowWarnings] = useState(true);

  if (!validationResult) {
    return null;
  }

  const hasErrors = validationResult.total_errors > 0;
  const hasWarnings = validationResult.total_warnings > 0;
  const recordsWithErrors = validationResult.record_errors.filter(r =>
    r.errors.some(e => e.severity === 'error')
  );
  const recordsWithWarnings = validationResult.record_errors.filter(r =>
    r.errors.some(e => e.severity === 'warning')
  );

  if (!hasErrors && !hasWarnings && validationResult.is_valid) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-green-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-green-900">
              Validation Passed
            </h3>
            <p className="mt-1 text-sm text-green-700">
              All {validationResult.total_records} records are valid and ready
              for submission.
            </p>
            {(validationResult.truncated_wages_count > 0 ||
              validationResult.capped_wages_count > 0) && (
              <div className="mt-3 space-y-1">
                {validationResult.truncated_wages_count > 0 && (
                  <p className="text-xs text-green-700 flex items-center gap-1">
                    <ScissorsIcon className="h-4 w-4" />
                    {validationResult.truncated_wages_count} record(s) had wages
                    truncated (cents removed)
                  </p>
                )}
                {validationResult.capped_wages_count > 0 && (
                  <p className="text-xs text-green-700 flex items-center gap-1">
                    <ArrowDownIcon className="h-4 w-4" />
                    {validationResult.capped_wages_count} record(s) had wages
                    capped at maximum
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Summary */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {hasErrors ? (
              <ExclamationCircleIcon className="h-8 w-8 text-red-500" />
            ) : (
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
            )}
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-medium text-gray-900">
              Validation Results
            </h3>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Total Records</p>
                <p className="text-lg font-semibold text-gray-900">
                  {validationResult.total_records}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Valid</p>
                <p className="text-lg font-semibold text-green-600">
                  {validationResult.valid_records}
                </p>
              </div>
              {hasErrors && (
                <div>
                  <p className="text-xs text-gray-500">Errors</p>
                  <p className="text-lg font-semibold text-red-600">
                    {validationResult.total_errors}
                  </p>
                </div>
              )}
              {hasWarnings && (
                <div>
                  <p className="text-xs text-gray-500">Warnings</p>
                  <p className="text-lg font-semibold text-yellow-600">
                    {validationResult.total_warnings}
                  </p>
                </div>
              )}
            </div>

            {/* Truncation/Capping Info */}
            {(validationResult.truncated_wages_count > 0 ||
              validationResult.capped_wages_count > 0) && (
              <div className="mt-3 flex items-start p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 space-y-1">
                  {validationResult.truncated_wages_count > 0 && (
                    <p>
                      {validationResult.truncated_wages_count} record(s) had
                      wages truncated (cents removed)
                    </p>
                  )}
                  {validationResult.capped_wages_count > 0 && (
                    <p>
                      {validationResult.capped_wages_count} record(s) had wages
                      capped at maximum
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submission-level Errors */}
      {validationResult.submission_errors.length > 0 && (
        <div className="p-6 border-b border-gray-200 bg-red-50">
          <h4 className="text-sm font-medium text-red-900 mb-3">
            Submission Errors
          </h4>
          <div className="space-y-2">
            {validationResult.submission_errors.map((error, index) => (
              <div
                key={index}
                className="flex items-start p-3 bg-white border border-red-200 rounded-lg"
              >
                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800">{error.message}</p>
                  {error.field && (
                    <p className="text-xs text-red-600 mt-1">
                      Field: {error.field}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Record Errors Section */}
      {recordsWithErrors.length > 0 && (
        <div className="border-b border-gray-200">
          <button
            type="button"
            onClick={() => setShowErrors(!showErrors)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              {showErrors ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
              )}
              <h4 className="text-sm font-medium text-gray-900">
                Record Errors ({recordsWithErrors.length})
              </h4>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {recordsWithErrors.length}
            </span>
          </button>

          {showErrors && (
            <div className="px-6 pb-6 space-y-3 bg-red-50">
              {recordsWithErrors.map(record => (
                <div
                  key={record.record_id}
                  className="p-4 bg-white border border-red-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {record.employee_name}
                        </p>
                        <span className="text-xs text-gray-500 font-mono">
                          NI: {record.ni_number}
                        </span>
                        {record.row_number && (
                          <span className="text-xs text-gray-500">
                            (Row {record.row_number})
                          </span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        {record.errors
                          .filter(e => e.severity === 'error')
                          .map((error, index) => (
                            <div key={index} className="flex items-start">
                              <ExclamationCircleIcon className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-red-700">
                                {error.field && (
                                  <span className="font-medium">
                                    {error.field}:{' '}
                                  </span>
                                )}
                                {error.message}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                    {onRecordClick && (
                      <button
                        type="button"
                        onClick={() => onRecordClick(record.record_id)}
                        className="ml-4 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Record Warnings Section */}
      {recordsWithWarnings.length > 0 && (
        <div className="border-b border-gray-200">
          <button
            type="button"
            onClick={() => setShowWarnings(!showWarnings)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              {showWarnings ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
              )}
              <h4 className="text-sm font-medium text-gray-900">
                Record Warnings ({recordsWithWarnings.length})
              </h4>
            </div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {recordsWithWarnings.length}
            </span>
          </button>

          {showWarnings && (
            <div className="px-6 pb-6 space-y-3 bg-yellow-50">
              {recordsWithWarnings.map(record => (
                <div
                  key={record.record_id}
                  className="p-4 bg-white border border-yellow-200 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {record.employee_name}
                        </p>
                        <span className="text-xs text-gray-500 font-mono">
                          NI: {record.ni_number}
                        </span>
                        {record.row_number && (
                          <span className="text-xs text-gray-500">
                            (Row {record.row_number})
                          </span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        {record.errors
                          .filter(e => e.severity === 'warning')
                          .map((error, index) => (
                            <div key={index} className="flex items-start">
                              <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-yellow-700">
                                {error.field && (
                                  <span className="font-medium">
                                    {error.field}:{' '}
                                  </span>
                                )}
                                {error.message}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                    {onRecordClick && (
                      <button
                        type="button"
                        onClick={() => onRecordClick(record.record_id)}
                        className="ml-4 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Generation Status */}
      <div className="p-6">
        {validationResult.can_generate ? (
          <div className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-900">
                Ready for Generation
              </p>
              <p className="text-xs text-green-700 mt-1">
                This submission can be generated into C10 format despite the
                warnings shown above.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">
                Cannot Generate C10 File
              </p>
              <p className="text-xs text-red-700 mt-1">
                Please fix all errors before generating the C10 file.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
